/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { shiftDate } from "../../shared/dates";
import { wallTime, zonedIso } from "./flags";
import type { HoursEntry } from "./types";

export type EntryDraft = {
  id?: number;
  staff_id: number;
  clock_in: string;
  clock_out: string | null;
  note: string | null;
  shift_id: number | null;
};

type Props = {
  name: string;
  timezone: string;
  /** The entry being edited, or the day/times to prefill a new one. */
  entry: HoursEntry | null;
  staffId: number;
  defaults: { date: string; start: string; end: string; shift_id: number | null };
  busy: boolean;
  onSave: (draft: EntryDraft) => void;
  onDelete: (id: number) => void;
  onClose: () => void;
};

// Add or correct a clock entry, in the shop's time. A clock-out earlier
// than the clock-in is the next day.
export const EntryModal = defineComponent<Props>({
  name: "EntryModal",
  props: ["name", "timezone", "entry", "staffId", "defaults", "busy", "onSave", "onDelete", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const e = props.entry;
    const start = e ? wallTime(e.clock_in, props.timezone) : { date: props.defaults.date, time: props.defaults.start };
    const date = ref(start.date);
    const inTime = ref(start.time);
    const outTime = ref(e ? (e.clock_out ? wallTime(e.clock_out, props.timezone).time : "") : props.defaults.end);
    const note = ref(e?.note ?? "");

    const save = () => {
      const clockIn = zonedIso(date.value, inTime.value, props.timezone);
      const clockOut = outTime.value
        ? zonedIso(outTime.value <= inTime.value ? shiftDate(date.value, 1) : date.value, outTime.value, props.timezone)
        : null;
      props.onSave({
        id: e?.id,
        staff_id: props.staffId,
        clock_in: clockIn,
        clock_out: clockOut,
        note: note.value.trim() || null,
        shift_id: e ? e.shift_id : props.defaults.shift_id,
      });
    };

    return () => (
      <LemmonModal title={props.name} onClose={props.onClose} renderButtons={() => null}>
        <div class="space-y-4 p-6">
          <h3 class="text-xl font-semibold">{t(e ? "workday.hours.edit_entry" : "workday.hours.add_entry")}</h3>
          <label class="form-control">
            <span class="label-text mb-1">{t("workday.hours.date")}</span>
            <input type="date" class="input input-bordered" v-model={date.value} />
          </label>
          <div class="grid grid-cols-2 gap-3">
            <label class="form-control">
              <span class="label-text mb-1">{t("workday.hours.clock_in")}</span>
              <input type="time" class="input input-bordered text-lg" v-model={inTime.value} />
            </label>
            <label class="form-control">
              <span class="label-text mb-1">{t("workday.hours.clock_out")}</span>
              <input type="time" class="input input-bordered text-lg" v-model={outTime.value} />
            </label>
          </div>
          <label class="form-control">
            <span class="label-text mb-1">{t("workday.hours.note")}</span>
            <input class="input input-bordered" placeholder={t("workday.hours.note_placeholder")} v-model={note.value} />
          </label>
          {e?.source === "link" && <p class="text-xs text-base-content/60">{t("workday.hours.from_link")}</p>}
          <div class="flex justify-between gap-3 pt-2">
            {e ? (
              <button class="btn btn-ghost text-error" disabled={props.busy} onClick={() => props.onDelete(e.id)}>
                {t("workday.common.delete")}
              </button>
            ) : (
              <span />
            )}
            <button class="btn btn-primary" disabled={props.busy || !date.value || !inTime.value} onClick={save}>
              {t("workday.common.save")}
            </button>
          </div>
        </div>
      </LemmonModal>
    );
  },
});
